import { useState, useEffect, useCallback } from "react";
import "./checkin.css";

// Standalone kiosk page — no navbar, no admin chrome
// URL: /checkin?classId=cls_wed&date=2026-05-07
// Teacher generates this URL from the admin dashboard and opens it on the studio iPad.

const API          = "https://iy-sp.org/api";
const KIOSK_TOKEN  = "admin123"; // TODO: move to REACT_APP_KIOSK_TOKEN env var pre-launch

function toLocalDateStr(date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function fmtTime(timeStr) {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":").map(Number);
  const ampm = h >= 12 ? "pm" : "am";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ampm}`;
}

export default function CheckIn() {
  const params  = new URLSearchParams(window.location.search);
  const classId = params.get("classId");
  const date    = params.get("date") ?? toLocalDateStr(new Date());

  const [bookings, setBookings] = useState([]);
  const [classInfo] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [checkedIn, setCheckedIn] = useState(new Set());
  const [lastChecked, setLastChecked] = useState(null); // shows brief name flash

  const fetchRoster = useCallback(async () => {
    if (!classId) { setLoading(false); return; }
    try {
      // Use the public booking endpoint filtered by classId + date
      const res  = await fetch(`${API}/bookings?classId=${classId}&date=${date}`, {
        headers: { Authorization: `Bearer ${KIOSK_TOKEN}` },
      });
      const data = await res.json();
      const confirmed = (data.bookings ?? []).filter(b => b.status === "confirmed");
      setBookings(confirmed);
      // Pre-populate already checked in
      const alreadyIn = new Set(confirmed.filter(b => b.checkedIn).map(b => b._id));
      setCheckedIn(alreadyIn);
    } catch { setBookings([]); }
    finally   { setLoading(false); }
  }, [classId, date]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    fetchRoster();
    const interval = setInterval(fetchRoster, 60_000);
    return () => clearInterval(interval);
  }, [fetchRoster]);

  const handleCheckin = async (booking) => {
    const isAlreadyIn = checkedIn.has(booking._id);
    // Optimistic update
    setCheckedIn(prev => {
      const next = new Set(prev);
      isAlreadyIn ? next.delete(booking._id) : next.add(booking._id);
      return next;
    });
    if (!isAlreadyIn) {
      setLastChecked(booking.studentName);
      setTimeout(() => setLastChecked(null), 2000);
    }
    // Persist to backend
    try {
      await fetch(`${API}/reports/checkin/${booking._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${KIOSK_TOKEN}` },
        body: JSON.stringify({ checkedIn: !isAlreadyIn }),
      });
    } catch {
      // Revert optimistic update on failure
      setCheckedIn(prev => {
        const next = new Set(prev);
        isAlreadyIn ? next.add(booking._id) : next.delete(booking._id);
        return next;
      });
    }
  };

  const filtered = bookings.filter(b =>
    search === "" || b.studentName.toLowerCase().includes(search.toLowerCase())
  );

  if (!classId) {
    return (
      <div className="checkin-wrap">
        <div className="checkin-no-class">
          <h2>No class selected</h2>
          <p>This kiosk URL needs a <code>?classId=</code> parameter.<br />Generate it from the Admin → Bookings tab.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="checkin-wrap">
      <div className="checkin-header">
        <div className="checkin-title">Iyengar Yoga · St. Petersburg</div>
        <div className="checkin-subtitle">Check in for today's class</div>
      </div>

      {classInfo && (
        <div className="checkin-class-info">
          <div className="checkin-class-name">{classInfo.title}</div>
          <div className="checkin-class-time">{date} · {fmtTime(classInfo.time)}</div>
        </div>
      )}

      {!classInfo && (
        <div className="checkin-class-info">
          <div className="checkin-class-name">Today's Class</div>
          <div className="checkin-class-time">{date}</div>
        </div>
      )}

      {lastChecked && (
        <div className="checkin-flash">✓ {lastChecked} checked in!</div>
      )}

      {bookings.length > 8 && (
        <input
          className="checkin-search"
          placeholder="Search your name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          autoComplete="off"
        />
      )}

      {loading && <p className="checkin-empty">Loading roster…</p>}

      {!loading && filtered.length === 0 && (
        <p className="checkin-empty">
          {search ? "No match — try a different spelling." : "No confirmed bookings for this class."}
        </p>
      )}

      <div className="checkin-list">
        {filtered.map(b => (
          <button
              key={b._id}
              className={`checkin-student-btn ${checkedIn.has(b._id) ? "is-checked" : ""}`}
              onClick={() => handleCheckin(b)}
            >
              <span>{b.studentName}</span>
              <span className="checkin-right">
                {!b.waiverSigned && (
                  <span className="checkin-waiver-warn" title="Waiver not signed">⚠</span>
                )}
                <span className="checkin-check-icon">{checkedIn.has(b._id) ? "✓" : ""}</span>
              </span>
            </button>
        ))}
      </div>

      <div className="checkin-footer">
        <span>{checkedIn.size} of {bookings.length} checked in</span>
        <span className="checkin-footer-refresh">Auto-refreshes every 60s</span>
      </div>
    </div>
  );
}
